import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api, formatCurrency, readFileAsDataUrl } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ColorPicker } from '@/components/ColorPicker';
import { ChevronLeft, Save, Plus, Pencil, Trash2, FileDown, Upload, Image as ImageIcon, X, Palette, Package, Users as UsersIcon, Building2, FileText, UserCheck, UserX, Sparkles, BadgeInfo, Paperclip, Leaf, ListChecks, Mail, Loader2, Lock, Eye, ArrowUp, ArrowDown, RefreshCw, FileSignature, ShieldCheck } from 'lucide-react';
import { useAuth, isAdmin } from '@/contexts/AuthContext';

const roleLabel = { admin: 'Super Admin', partner: 'B.O.', area_manager: 'Area Manager', agent: 'Agente' };
const statusBadge = { pending: { v: 'outline', l: 'In attesa' }, active: { v: 'default', l: 'Attivo' }, disabled: { v: 'destructive', l: 'Disabilitato' } };

const emptyProduct = { company_id: '', category: 'fotovoltaico_accumulo', brand: '', model: '', description: '', power: '', unit_price: 0, notes: '', active: true, warranty_years: '', efficiency: '', annual_production_kwh: 0, datasheet_data_url: '', datasheet_filename: '', datasheets: [], thumbnail_data_url: '', is_custom: false };
const emptyUser = { full_name: '', email: '', phone: '', password: '', role: 'agent', company_id: '', status: 'active', agent_permissions: { can_send_email: true, can_view_prices: true } };

export default function PartnerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const admin = isAdmin(authUser);

  // Company state
  const [company, setCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState(null);
  const [savingCompany, setSavingCompany] = useState(false);

  // Catalog state
  const [products, setProducts] = useState([]);
  const [productOpen, setProductOpen] = useState(false);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [editProductId, setEditProductId] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [productSearch, setProductSearch] = useState('');
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // all | active | inactive
  const [filterCustom, setFilterCustom] = useState('all'); // all | custom | catalog
  const [filterDatasheet, setFilterDatasheet] = useState('all'); // all | with | without
  const [filterPhase, setFilterPhase] = useState('all'); // all | monofase | trifase
  const [powerMin, setPowerMin] = useState('');
  const [powerMax, setPowerMax] = useState('');
  const [kwhMin, setKwhMin] = useState('');
  const [kwhMax, setKwhMax] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [productPage, setProductPage] = useState(1);
  const PRODUCT_PAGE_SIZE = 20;
  const [datasheetsDrag, setDatasheetsDrag] = useState(false);

  // Users state
  const [users, setUsers] = useState([]);
  const [userOpen, setUserOpen] = useState(false);
  const [userForm, setUserForm] = useState(emptyUser);
  const [editUserId, setEditUserId] = useState(null);
  const [filterUserStatus, setFilterUserStatus] = useState('all');

  // Meta
  const [options, setOptions] = useState({ product_categories: [], payment_methods: [] });

  // Listino import Excel
  const importFileRef = useRef(null);
  const quoteCoverRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Stato di generazione PDF vuoto (evita doppi click) - locale al profilo aziendale
  const [generatingBlank, setGeneratingBlank] = useState(false);
  const generateBlank = async () => {
    if (generatingBlank) return;
    setGeneratingBlank(true);
    const tId = toast.loading('Generazione PDF contratto stampabile vuoto in corso, attendere…');
    try {
      await api.openCompanyBlankPdf(id);
      toast.success('PDF generato', { id: tId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore generazione PDF', { id: tId });
    } finally {
      setGeneratingBlank(false);
    }
  };

  const handleExportXlsx = () => api.exportListinoXlsx(id, company?.name || 'listino');

  const handleImportXlsx = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const res = await api.importListinoXlsx(id, file);
      setImportResult(res);
      toast.success(`Import completato: ${res.created} creati, ${res.updated} aggiornati${res.error_count ? `, ${res.error_count} righe con errori` : ''}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore durante l\'import');
    } finally {
      setImporting(false);
    }
  };

  const handleToggleAgentVisibility = async (productId, visible) => {
    try {
      await api.setAgentVisibility(id, productId, visible);
      setCompany(prev => {
        const hidden = new Set(prev?.agent_hidden_product_ids || []);
        if (visible) hidden.delete(productId); else hidden.add(productId);
        return { ...prev, agent_hidden_product_ids: [...hidden] };
      });
      toast.success(visible ? 'Prodotto visibile agli agenti' : 'Prodotto nascosto agli agenti');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento visibilità');
    }
  };

  const load = async () => {
    try {
      const [c, prods, usrs, opts] = await Promise.all([
        api.getCompany(id),
        api.listProducts({ company_id: id }),
        api.listUsers({ company_id: id }),
        api.getOptions(),
      ]);
      setCompany(c);
      setCompanyForm(c);
      setProducts(prods);
      setUsers(usrs);
      setOptions(opts);
    } catch (e) {
      toast.error('Azienda non trovata');
      navigate(admin ? '/partners' : '/');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const catMap = useMemo(() => Object.fromEntries((options.product_categories || []).map(c => [c.key, c.label])), [options]);

  // ------- Company tab -------
  const handleSaveCompany = async () => {
    setSavingCompany(true);
    try {
      const updated = await api.updateCompany(id, companyForm);
      setCompany(updated);
      setCompanyForm(updated);
      toast.success('Dati azienda salvati');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSavingCompany(false); }
  };

  const [extractedPalette, setExtractedPalette] = useState([]);
  const [extractingPalette, setExtractingPalette] = useState(false);

  const extractPalette = async (dataUrl) => {
    if (!dataUrl) { setExtractedPalette([]); return; }
    setExtractingPalette(true);
    try {
      const res = await api.extractPalette(dataUrl);
      setExtractedPalette(res.colors || []);
    } catch (e) {
      toast.error('Impossibile estrarre i colori dal logo');
      setExtractedPalette([]);
    } finally {
      setExtractingPalette(false);
    }
  };

  const handleLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error('Logo troppo grande (max 1.5MB)'); return; }
    const dataUrl = await readFileAsDataUrl(file);
    setCompanyForm(f => ({ ...f, logo_data_url: dataUrl }));
    // Kickstart palette extraction
    extractPalette(dataUrl);
  };

  const applyPaletteColor = (hex, target) => {
    setCompanyForm(f => ({ ...f, [target]: hex }));
    toast.success(`${target === 'primary_color' ? 'Colore primario' : 'Colore accent'} applicato: ${hex}`);
  };

  // ------- Product tab -------
  const handleNewProduct = () => {
    setEditProductId(null);
    setProductForm({ ...emptyProduct, company_id: id });
    setProductOpen(true);
  };
  const handleNewCustomProduct = () => {
    setEditProductId(null);
    setProductForm({
      ...emptyProduct,
      company_id: id,
      category: 'accessorio',
      brand: '',
      model: 'Voce personalizzata',
      description: 'Descrizione modificabile in fase di preventivo/contratto',
      is_custom: true,
      unit_price: 0,
    });
    setProductOpen(true);
  };
  const addDatasheetFiles = async (files) => {
    const pdfFiles = files.filter(f => f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name)));
    if (files.length && pdfFiles.length === 0) { toast.error('Sono ammessi solo file PDF'); return; }
    let added = 0;
    for (const file of pdfFiles) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`"${file.name}" supera i 5MB e verrà ignorato`);
        continue;
      }
      try {
        const dataUrl = await readFileAsDataUrl(file);
        setProductForm(f => ({
          ...f,
          datasheets: [...(f.datasheets || []), { data_url: dataUrl, filename: file.name }],
        }));
        added += 1;
      } catch {
        toast.error(`Impossibile leggere "${file.name}"`);
      }
    }
    if (added > 0) toast.success(added === 1 ? 'Scheda tecnica aggiunta' : `${added} schede tecniche aggiunte`);
  };

  const moveDatasheet = (idx, dir) => {
    setProductForm(f => {
      const arr = [...(f.datasheets || [])];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, datasheets: arr };
    });
  };

  const replaceDatasheet = async (idx, file) => {
    if (!file) return;
    if (!(file.type === 'application/pdf' || /\.pdf$/i.test(file.name))) { toast.error('Sono ammessi solo file PDF'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Il PDF supera i 5MB'); return; }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setProductForm(f => {
        const arr = [...(f.datasheets || [])];
        arr[idx] = { data_url: dataUrl, filename: file.name };
        return { ...f, datasheets: arr };
      });
      toast.success('Scheda tecnica sostituita');
    } catch { toast.error('Impossibile leggere il file'); }
  };

  const previewDatasheet = (ds) => {
    if (!ds?.data_url) return;
    try {
      // Converti il data URL in Blob per aprire in nuova tab (evita limiti di lunghezza URL)
      const [meta, base64] = ds.data_url.split(',');
      const mime = /data:(.*?);base64/.exec(meta || '')?.[1] || 'application/pdf';
      const bin = atob(base64 || '');
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Impossibile aprire l\'anteprima'); }
  };

  const handleEditProduct = (p) => {
    setEditProductId(p.id);
    // Migrazione retro-compatibile: se esiste solo il vecchio datasheet_data_url e non c'è ancora l'array,
    // lo convertiamo automaticamente nella nuova struttura `datasheets`.
    let datasheets = Array.isArray(p.datasheets) ? [...p.datasheets] : [];
    if (datasheets.length === 0 && p.datasheet_data_url) {
      datasheets = [{ data_url: p.datasheet_data_url, filename: p.datasheet_filename || 'scheda_tecnica.pdf' }];
    }
    setProductForm({ ...emptyProduct, ...p, datasheets, datasheet_data_url: '', datasheet_filename: '' });
    setProductOpen(true);
  };
  const handleSaveProduct = async () => {
    if (!productForm.description?.trim() && !productForm.model?.trim() && !productForm.brand?.trim()) {
      toast.error('Inserisci almeno marca, modello o descrizione'); return;
    }
    try {
      const dsList = Array.isArray(productForm.datasheets) ? productForm.datasheets : [];
      const first = dsList[0] || null;
      const payload = {
        ...productForm,
        company_id: id,
        unit_price: Number(productForm.unit_price) || 0,
        datasheets: dsList,
        // Mirror della prima scheda nei campi legacy per compatibilità retro (PDF / esistenti flussi)
        datasheet_data_url: first?.data_url || '',
        datasheet_filename: first?.filename || '',
      };
      if (editProductId) { await api.updateProduct(editProductId, payload); toast.success('Prodotto aggiornato'); }
      else { await api.createProduct(payload); toast.success('Prodotto creato'); }
      setProductOpen(false); setEditProductId(null); setProductForm(emptyProduct); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const handleDeleteProduct = async (pid) => { try { await api.deleteProduct(pid); toast.success('Eliminato'); load(); } catch { toast.error('Errore'); } };

  // Estrae il primo valore numerico presente in una stringa (es. "6 kW" -> 6, "465 Wp" -> 465)
  const parseNumericPower = (val) => {
    if (val == null) return null;
    const s = String(val).replace(',', '.');
    const m = s.match(/-?\d+(?:\.\d+)?/);
    if (!m) return null;
    let n = parseFloat(m[0]);
    if (Number.isNaN(n)) return null;
    // Se il valore \u00e8 espresso in Wp e non in kWp, normalizziamo in kWp per confronto omogeneo
    if (/wp\b|watt/i.test(s) && !/kwp?\b/i.test(s)) n = n / 1000;
    return n;
  };

  const brandOptions = useMemo(() => {
    const set = new Set();
    products.forEach(p => { const b = (p.brand || '').trim(); if (b) set.add(b); });
    return [...set].sort((a, b) => a.localeCompare(b, 'it'));
  }, [products]);

  const filteredProducts = products.filter(p => {
    if (filterCategory !== 'all' && p.category !== filterCategory) return false;
    if (filterBrand !== 'all' && (p.brand || '').trim() !== filterBrand) return false;
    if (filterStatus === 'active' && !p.active) return false;
    if (filterStatus === 'inactive' && p.active) return false;
    if (filterCustom === 'custom' && !p.is_custom) return false;
    if (filterCustom === 'catalog' && p.is_custom) return false;
    if (filterPhase !== 'all') {
      if (!p.phase_type || p.phase_type !== filterPhase) return false;
    }
    const hasDs = !!p.datasheet_data_url || (Array.isArray(p.datasheets) && p.datasheets.length > 0);
    if (filterDatasheet === 'with' && !hasDs) return false;
    if (filterDatasheet === 'without' && hasDs) return false;
    const pw = parseNumericPower(p.power);
    if (powerMin !== '') {
      const min = parseFloat(String(powerMin).replace(',', '.'));
      if (!Number.isNaN(min) && (pw == null || pw < min)) return false;
    }
    if (powerMax !== '') {
      const max = parseFloat(String(powerMax).replace(',', '.'));
      if (!Number.isNaN(max) && (pw == null || pw > max)) return false;
    }
    const kwh = Number(p.battery_kwh) || 0;
    if (kwhMin !== '') {
      const min = parseFloat(String(kwhMin).replace(',', '.'));
      if (!Number.isNaN(min) && kwh < min) return false;
    }
    if (kwhMax !== '') {
      const max = parseFloat(String(kwhMax).replace(',', '.'));
      if (!Number.isNaN(max) && kwh > max) return false;
    }
    const price = Number(p.unit_price) || 0;
    if (priceMin !== '') {
      const min = parseFloat(String(priceMin).replace(',', '.'));
      if (!Number.isNaN(min) && price < min) return false;
    }
    if (priceMax !== '') {
      const max = parseFloat(String(priceMax).replace(',', '.'));
      if (!Number.isNaN(max) && price > max) return false;
    }
    if (productSearch) {
      const t = productSearch.toLowerCase();
      if (![p.brand, p.model, p.description].some(s => (s || '').toLowerCase().includes(t))) return false;
    }
    return true;
  });

  const activeFiltersCount =
    (filterCategory !== 'all' ? 1 : 0) +
    (filterBrand !== 'all' ? 1 : 0) +
    (filterStatus !== 'all' ? 1 : 0) +
    (filterCustom !== 'all' ? 1 : 0) +
    (filterDatasheet !== 'all' ? 1 : 0) +
    (filterPhase !== 'all' ? 1 : 0) +
    (powerMin !== '' ? 1 : 0) +
    (powerMax !== '' ? 1 : 0) +
    (kwhMin !== '' ? 1 : 0) +
    (kwhMax !== '' ? 1 : 0) +
    (priceMin !== '' ? 1 : 0) +
    (priceMax !== '' ? 1 : 0) +
    (productSearch ? 1 : 0);

  const resetFilters = () => {
    setFilterCategory('all'); setFilterBrand('all'); setFilterStatus('all');
    setFilterCustom('all'); setFilterDatasheet('all'); setFilterPhase('all');
    setPowerMin(''); setPowerMax(''); setKwhMin(''); setKwhMax('');
    setPriceMin(''); setPriceMax(''); setProductSearch('');
    setProductPage(1);
  };

  // Reset page quando cambiano filtri
  useEffect(() => { setProductPage(1); }, [
    filterCategory, filterBrand, filterStatus, filterCustom, filterDatasheet,
    filterPhase, powerMin, powerMax, kwhMin, kwhMax, priceMin, priceMax, productSearch, products.length,
  ]);

  // Paginazione (max 20 per pagina)
  const totalProductPages = Math.max(1, Math.ceil(filteredProducts.length / PRODUCT_PAGE_SIZE));
  const currentProductPage = Math.min(productPage, totalProductPages);
  const pagedProducts = filteredProducts.slice((currentProductPage - 1) * PRODUCT_PAGE_SIZE, currentProductPage * PRODUCT_PAGE_SIZE);

  // ------- Users tab -------
  const handleNewUser = () => {
    setEditUserId(null);
    setUserForm({ ...emptyUser, company_id: id, role: 'agent' });
    setUserOpen(true);
  };
  const handleEditUser = (u) => { setEditUserId(u.id); setUserForm({ ...emptyUser, ...u, password: '' }); setUserOpen(true); };
  const handleSaveUser = async () => {
    try {
      if (editUserId) {
        const payload = { ...userForm };
        if (!payload.password) delete payload.password;
        await api.updateUser(editUserId, payload);
        toast.success('Utente aggiornato');
      } else {
        if (!userForm.password || userForm.password.length < 6) { toast.error('Password minima 6 caratteri'); return; }
        await api.createUser({ ...userForm, company_id: id });
        toast.success('Utente creato');
      }
      setUserOpen(false); setUserForm(emptyUser); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const handleApprove = async (u) => { try { await api.updateUser(u.id, { status: 'active' }); toast.success('Approvato'); load(); } catch { toast.error('Errore'); } };
  const handleDisableUser = async (u) => { try { await api.updateUser(u.id, { status: 'disabled' }); toast.success('Disabilitato'); load(); } catch { toast.error('Errore'); } };
  const handleDeleteUser = async (u) => { try { await api.deleteUser(u.id); toast.success('Eliminato'); load(); } catch { toast.error('Errore'); } };

  const filteredUsers = users.filter(u => filterUserStatus === 'all' || u.status === filterUserStatus);

  // ------- Branding tab -------
  const handleSavebranding = async () => {
    setSavingCompany(true);
    try {
      const updated = await api.updateCompany(id, {
        primary_color: companyForm.primary_color,
        accent_color: companyForm.accent_color,
        logo_data_url: companyForm.logo_data_url,
        stamp_signature_data_url: companyForm.stamp_signature_data_url,
      });
      setCompany(updated);
      toast.success('Branding aggiornato');
    } catch { toast.error('Errore'); } finally { setSavingCompany(false); }
  };

  if (!company || !companyForm) return <div className="p-10 text-center text-muted-foreground">Caricamento...</div>;

  return (
    <div>
      <PageHeader
        title={
          <div className="flex items-center gap-3">
            {company.logo_data_url ? <img src={company.logo_data_url} alt="" className="size-10 rounded-lg border bg-white object-contain p-1" /> : <div className="size-10 rounded-lg border bg-secondary grid place-items-center"><Building2 className="size-5 text-muted-foreground" /></div>}
            <span>{company.name}</span>
          </div>
        }
        description={[company.city, company.province, company.vat_number].filter(Boolean).join(' · ')}
        actions={
          <div className="flex gap-2">
            {admin && <Button asChild variant="outline" size="sm"><Link to="/partners"><ChevronLeft className="size-4 mr-1" /> Aziende</Link></Button>}
            <Button variant="outline" size="sm" onClick={generateBlank} disabled={generatingBlank} data-testid="partner-profile-blank-pdf">
              {generatingBlank ? <Loader2 className="size-4 mr-2 animate-spin" /> : <FileDown className="size-4 mr-2" />}
              {generatingBlank ? 'Generazione in corso…' : 'PDF Contratto Stampabile Vuoto'}
            </Button>
          </div>
        }
      />

      <Tabs defaultValue="dati" className="space-y-4">
        <TabsList className={`h-auto flex flex-wrap justify-start gap-1 ${admin ? 'lg:grid lg:w-[820px] lg:grid-cols-5' : 'lg:grid lg:w-[500px] lg:grid-cols-3'}`}>
          <TabsTrigger value="dati" data-testid="tab-dati"><Building2 className="size-4 mr-1.5" />Dati Azienda</TabsTrigger>
          <TabsTrigger value="listino" data-testid="tab-listino"><Package className="size-4 mr-1.5" />Listino ({products.length})</TabsTrigger>
          <TabsTrigger value="agenti" data-testid="tab-agenti"><UsersIcon className="size-4 mr-1.5" />Agenti ({users.length})</TabsTrigger>
          {admin && <TabsTrigger value="branding" data-testid="tab-branding"><Palette className="size-4 mr-1.5" />Branding</TabsTrigger>}
          {admin && <TabsTrigger value="preventivo" data-testid="tab-preventivo"><FileText className="size-4 mr-1.5" />Preventivo</TabsTrigger>}
        </TabsList>

        {/* ===== TAB DATI AZIENDA ===== */}
        <TabsContent value="dati">
          <Card>
            <CardHeader><CardTitle className="text-base">Anagrafica azienda</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {!admin && (
                <Alert data-testid="partner-restricted-alert">
                  <Lock className="size-4" />
                  <AlertTitle>Dati gestiti da Scintilla Hub</AlertTitle>
                  <AlertDescription>
                    I dati anagrafici e fiscali (ragione sociale, P.IVA, sede, PEC, SDI...) sono gestiti da Scintilla Hub: contattaci per modificarli.
                    Puoi aggiornare autonomamente contatti, branding e impostazioni preventivo.
                  </AlertDescription>
                </Alert>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldI label="Ragione sociale *"><Input value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} disabled={!admin} data-testid="profile-company-name" /></FieldI>
                <FieldI label="Forma giuridica"><Input value={companyForm.legal_form || ''} onChange={(e) => setCompanyForm({ ...companyForm, legal_form: e.target.value })} disabled={!admin} /></FieldI>
                <FieldI label="Indirizzo" full><Input value={companyForm.address || ''} onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })} disabled={!admin} /></FieldI>
                <FieldI label="Città"><Input value={companyForm.city || ''} onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })} disabled={!admin} /></FieldI>
                <div className="grid grid-cols-2 gap-2">
                  <FieldI label="CAP"><Input value={companyForm.cap || ''} onChange={(e) => setCompanyForm({ ...companyForm, cap: e.target.value })} disabled={!admin} /></FieldI>
                  <FieldI label="Provincia"><Input value={companyForm.province || ''} onChange={(e) => setCompanyForm({ ...companyForm, province: e.target.value })} maxLength={2} disabled={!admin} /></FieldI>
                </div>
                <FieldI label="Partita IVA"><Input value={companyForm.vat_number || ''} onChange={(e) => setCompanyForm({ ...companyForm, vat_number: e.target.value })} className="font-mono" disabled={!admin} /></FieldI>
                <FieldI label="Codice Fiscale"><Input value={companyForm.fiscal_code || ''} onChange={(e) => setCompanyForm({ ...companyForm, fiscal_code: e.target.value })} className="font-mono" disabled={!admin} /></FieldI>
                <FieldI label="Telefono"><Input value={companyForm.phone || ''} onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })} /></FieldI>
                <FieldI label="Email"><Input type="email" value={companyForm.email || ''} onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })} /></FieldI>
                <FieldI label="PEC"><Input type="email" value={companyForm.pec || ''} onChange={(e) => setCompanyForm({ ...companyForm, pec: e.target.value })} placeholder="pec@azienda.it" disabled={!admin} data-testid="profile-pec" /></FieldI>
                <FieldI label="Codice destinatario SDI">
                  <Input
                    value={companyForm.sdi_code || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, sdi_code: e.target.value.toUpperCase() })}
                    maxLength={7}
                    className="font-mono uppercase"
                    placeholder="XXXXXXX"
                    disabled={!admin}
                    data-testid="profile-sdi-code"
                  />
                </FieldI>
                <FieldI label="Codice ATECO">
                  <Input
                    value={companyForm.ateco_code || ''}
                    onChange={(e) => setCompanyForm({ ...companyForm, ateco_code: e.target.value })}
                    className="font-mono"
                    placeholder="Es. 43.21.01"
                    disabled={!admin}
                    data-testid="profile-ateco-code"
                  />
                </FieldI>
                <FieldI label="Sito web"><Input value={companyForm.website || ''} onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })} /></FieldI>
                <FieldI label="REA"><Input value={companyForm.rea || ''} onChange={(e) => setCompanyForm({ ...companyForm, rea: e.target.value })} disabled={!admin} /></FieldI>
                <FieldI label="Capitale sociale (€)"><Input value={companyForm.share_capital || ''} onChange={(e) => setCompanyForm({ ...companyForm, share_capital: e.target.value })} disabled={!admin} /></FieldI>
              </div>
              <Separator />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCompanyForm(company)}>Annulla</Button>
                <Button onClick={handleSaveCompany} disabled={savingCompany} data-testid="profile-save-company"><Save className="size-4 mr-2" />Salva dati azienda</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TAB LISTINO ===== */}
        <TabsContent value="listino">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Listino prodotti</CardTitle>
              <div className="flex gap-2">
                {admin && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleExportXlsx} data-testid="profile-export-listino">
                      <FileDown className="size-4 mr-2" />Scarica Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => importFileRef.current?.click()} disabled={importing} data-testid="profile-import-listino">
                      {importing ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Upload className="size-4 mr-2" />}Importa Excel
                    </Button>
                    <input ref={importFileRef} type="file" accept=".xlsx" className="hidden" onChange={handleImportXlsx} data-testid="profile-import-file-input" />
                  </>
                )}
                <Button variant="outline" size="sm" onClick={handleNewCustomProduct} data-testid="profile-product-create-custom"><Sparkles className="size-4 mr-2" />Voce personalizzabile</Button>
                <Button size="sm" onClick={handleNewProduct} data-testid="profile-product-create"><Plus className="size-4 mr-2" />Nuovo prodotto</Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="px-4 pb-4 flex flex-col gap-3">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <Input placeholder="Cerca marca, modello, descrizione..." value={productSearch} onChange={(e) => setProductSearch(e.target.value)} className="max-w-sm" data-testid="profile-products-search" />
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-full md:w-[220px]" data-testid="filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le categorie</SelectItem>
                      {(options.product_categories || []).map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterBrand} onValueChange={setFilterBrand}>
                    <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-brand"><SelectValue placeholder="Marca" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le marche</SelectItem>
                      {brandOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {activeFiltersCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={resetFilters} className="text-xs" data-testid="filter-reset">
                      <X className="size-3 mr-1" /> Reset filtri ({activeFiltersCount})
                    </Button>
                  )}
                </div>
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full md:w-[170px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Attivi + Inattivi</SelectItem>
                      <SelectItem value="active">Solo attivi</SelectItem>
                      <SelectItem value="inactive">Solo inattivi</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterCustom} onValueChange={setFilterCustom}>
                    <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-custom"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Standard + Personalizzabili</SelectItem>
                      <SelectItem value="catalog">Solo standard</SelectItem>
                      <SelectItem value="custom">Solo personalizzabili</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterDatasheet} onValueChange={setFilterDatasheet}>
                    <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-datasheet"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le schede</SelectItem>
                      <SelectItem value="with">Con scheda tecnica</SelectItem>
                      <SelectItem value="without">Senza scheda tecnica</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="inline-flex rounded-lg border p-0.5 bg-secondary/30 h-10" data-testid="filter-phase">
                    {[
                      { key: 'all', label: 'Tutte' },
                      { key: 'monofase', label: 'Monofase' },
                      { key: 'trifase', label: 'Trifase' },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setFilterPhase(opt.key)}
                        className={`px-3 h-9 rounded-md text-xs font-medium transition-colors ${filterPhase === opt.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                        data-testid={`filter-phase-${opt.key}`}
                      >{opt.label}</button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">kWp (imp.):</span>
                    <Input
                      type="number"
                      inputMode="decimal"
                      placeholder="min"
                      value={powerMin}
                      onChange={(e) => setPowerMin(e.target.value)}
                      className="w-20 tabular-nums"
                      data-testid="filter-power-min"
                    />
                    <span className="text-muted-foreground">—</span>
                    <Input
                      type="number"
                      inputMode="decimal"
                      placeholder="max"
                      value={powerMax}
                      onChange={(e) => setPowerMax(e.target.value)}
                      className="w-20 tabular-nums"
                      data-testid="filter-power-max"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">kWh (batt.):</span>
                    <Input type="number" inputMode="decimal" placeholder="min" value={kwhMin} onChange={(e) => setKwhMin(e.target.value)} className="w-20 tabular-nums" data-testid="filter-kwh-min" />
                    <span className="text-muted-foreground">—</span>
                    <Input type="number" inputMode="decimal" placeholder="max" value={kwhMax} onChange={(e) => setKwhMax(e.target.value)} className="w-20 tabular-nums" data-testid="filter-kwh-max" />
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">€ prezzo:</span>
                    <Input type="number" inputMode="decimal" placeholder="min" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} className="w-24 tabular-nums" data-testid="filter-price-min" />
                    <span className="text-muted-foreground">—</span>
                    <Input type="number" inputMode="decimal" placeholder="max" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} className="w-24 tabular-nums" data-testid="filter-price-max" />
                  </div>
                  <div className="text-xs text-muted-foreground md:ml-auto">
                    {filteredProducts.length} di {products.length} · pag. {currentProductPage}/{totalProductPages}
                  </div>
                </div>
              </div>
              {filteredProducts.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="mx-auto size-12 rounded-full bg-secondary grid place-items-center mb-3"><Package className="size-5 text-muted-foreground" /></div>
                  <div className="text-sm font-medium">Listino vuoto</div>
                  <div className="text-xs text-muted-foreground mb-4">Aggiungi prodotti a catalogo o crea una voce personalizzabile che l&apos;operatore potrà completare al momento</div>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={handleNewCustomProduct} variant="outline" size="sm"><Sparkles className="size-4 mr-2" />Voce personalizzabile</Button>
                    <Button onClick={handleNewProduct} size="sm"><Plus className="size-4 mr-2" />Nuovo prodotto</Button>
                  </div>
                </div>
              ) : (
                <Table data-testid="profile-products-table">
                  <TableHeader><TableRow>
                    <TableHead>Categoria</TableHead><TableHead>Marca / Modello</TableHead><TableHead>Descrizione</TableHead>
                    <TableHead className="text-right">Prezzo</TableHead><TableHead>Stato</TableHead><TableHead>Visibile agenti</TableHead><TableHead className="text-right">Azioni</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {pagedProducts.map(p => (
                      <TableRow key={p.id} data-testid={`profile-product-row-${p.id}`}>
                        <TableCell><Badge variant="secondary">{catMap[p.category] || p.category}</Badge></TableCell>
                        <TableCell>
                          <div className="font-medium flex items-center gap-2">{p.brand} {p.model}
                            {p.is_custom && <Badge variant="outline" className="gap-1 text-[10px] border-accent text-accent"><Sparkles className="size-3" />Personalizzabile</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground">{p.power}</div>
                        </TableCell>
                        <TableCell className="max-w-sm truncate text-sm">
                          {p.description}
                          {(p.datasheet_data_url || (Array.isArray(p.datasheets) && p.datasheets.length > 0)) && <Badge variant="outline" className="ml-2 gap-1 text-[10px]"><Paperclip className="size-3" />{(Array.isArray(p.datasheets) ? p.datasheets.length : 0) > 1 ? `${p.datasheets.length} schede` : 'Scheda'}</Badge>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{formatCurrency(p.unit_price)}</TableCell>
                        <TableCell>{p.active ? <Badge>Attivo</Badge> : <Badge variant="outline">Inattivo</Badge>}</TableCell>
                        <TableCell>
                          <Switch
                            checked={!(company?.agent_hidden_product_ids || []).includes(p.id)}
                            onCheckedChange={(v) => handleToggleAgentVisibility(p.id, v)}
                            data-testid={`agent-visibility-${p.id}`}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => handleEditProduct(p)}><Pencil className="size-4" /></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button variant="ghost" size="icon"><Trash2 className="size-4 text-destructive" /></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Eliminare il prodotto?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteProduct(p.id)}>Elimina</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {totalProductPages > 1 && (
                <div className="p-3 border-t flex items-center justify-between gap-2 flex-wrap" data-testid="profile-products-pagination">
                  <div className="text-xs text-muted-foreground">
                    Mostrando {(currentProductPage - 1) * PRODUCT_PAGE_SIZE + 1}-{Math.min(currentProductPage * PRODUCT_PAGE_SIZE, filteredProducts.length)} di {filteredProducts.length}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" disabled={currentProductPage === 1} onClick={() => setProductPage(1)}>«</Button>
                    <Button variant="outline" size="sm" disabled={currentProductPage === 1} onClick={() => setProductPage(p => Math.max(1, p - 1))}>‹ Precedente</Button>
                    <span className="px-3 text-sm tabular-nums">Pag. {currentProductPage} / {totalProductPages}</span>
                    <Button variant="outline" size="sm" disabled={currentProductPage === totalProductPages} onClick={() => setProductPage(p => Math.min(totalProductPages, p + 1))}>Successiva ›</Button>
                    <Button variant="outline" size="sm" disabled={currentProductPage === totalProductPages} onClick={() => setProductPage(totalProductPages)}>»</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TAB AGENTI ===== */}
        <TabsContent value="agenti">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Agenti e responsabili</CardTitle>
              <Button size="sm" onClick={handleNewUser} data-testid="profile-user-create"><Plus className="size-4 mr-2" />Nuovo utente</Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="px-4 pb-4 flex items-center gap-3">
                <Select value={filterUserStatus} onValueChange={setFilterUserStatus}>
                  <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem><SelectItem value="pending">In attesa</SelectItem><SelectItem value="active">Attivo</SelectItem><SelectItem value="disabled">Disabilitato</SelectItem></SelectContent>
                </Select>
                <div className="flex-1" />
                <div className="text-xs text-muted-foreground">{filteredUsers.length} utenti</div>
              </div>
              {filteredUsers.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="mx-auto size-12 rounded-full bg-secondary grid place-items-center mb-3"><UsersIcon className="size-5 text-muted-foreground" /></div>
                  <div className="text-sm font-medium">Nessun agente</div>
                  <div className="text-xs text-muted-foreground mb-4">Aggiungi il primo responsabile o agente per questa azienda</div>
                  <Button size="sm" onClick={handleNewUser}><Plus className="size-4 mr-2" />Nuovo utente</Button>
                </div>
              ) : (
                <Table data-testid="profile-users-table">
                  <TableHeader><TableRow>
                    <TableHead>Utente</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {filteredUsers.map(u => (
                      <TableRow key={u.id} data-testid={`profile-user-row-${u.id}`}>
                        <TableCell><div className="font-medium">{u.full_name || '—'}</div><div className="text-xs text-muted-foreground">{u.phone}</div></TableCell>
                        <TableCell className="font-mono text-xs">{u.email}</TableCell>
                        <TableCell><Badge variant="secondary">{roleLabel[u.role]}</Badge></TableCell>
                        <TableCell><Badge variant={statusBadge[u.status]?.v || 'outline'}>{statusBadge[u.status]?.l || u.status}</Badge></TableCell>
                        <TableCell className="text-right space-x-1">
                          {u.status === 'pending' && <Button size="sm" onClick={() => handleApprove(u)} data-testid={`profile-user-approve-${u.id}`}><UserCheck className="size-4 mr-1" />Approva</Button>}
                          <Button variant="ghost" size="icon" onClick={() => handleEditUser(u)}><Pencil className="size-4" /></Button>
                          {u.status === 'active' && <Button variant="ghost" size="icon" onClick={() => handleDisableUser(u)} title="Disabilita"><UserX className="size-4" /></Button>}
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button variant="ghost" size="icon"><Trash2 className="size-4 text-destructive" /></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Eliminare l’utente?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteUser(u)}>Elimina</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TAB PERSONALIZZAZIONE PDF (solo Admin Scintilla) ===== */}
        {admin && (
        <TabsContent value="branding">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Logo, colori e identità per il PDF</CardTitle></CardHeader>
                <CardContent className="space-y-5">
                  <div className="space-y-2">
                    <Label>Logo aziendale</Label>
                    <div className="flex items-center gap-3">
                      <div className="size-24 rounded-lg border bg-white grid place-items-center overflow-hidden p-2">
                        {companyForm.logo_data_url ? <img src={companyForm.logo_data_url} alt="logo" className="max-h-full max-w-full object-contain" /> : <ImageIcon className="size-6 text-muted-foreground" />}
                      </div>
                      <div className="flex-1">
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogo} className="text-xs" data-testid="profile-logo-upload" />
                        <div className="text-xs text-muted-foreground mt-1">Formato consigliato 400×140px, sfondo trasparente. PNG/JPEG/WebP, max 1.5MB.</div>
                        {companyForm.logo_data_url && !extractedPalette.length && !extractingPalette && (
                          <Button
                            type="button" variant="link" size="sm"
                            className="h-auto p-0 mt-1 text-xs text-primary"
                            onClick={() => extractPalette(companyForm.logo_data_url)}
                            data-testid="profile-extract-palette"
                          >
                            <Sparkles className="size-3 mr-1" />Estrai colori dal logo
                          </Button>
                        )}
                      </div>
                      {companyForm.logo_data_url && <Button type="button" variant="ghost" size="icon" onClick={() => { setCompanyForm({ ...companyForm, logo_data_url: '' }); setExtractedPalette([]); }}><X className="size-4" /></Button>}
                    </div>

                    {/* Palette estratta dal logo */}
                    {(extractingPalette || extractedPalette.length > 0) && (
                      <div className="mt-3 rounded-lg border border-dashed p-3 bg-secondary/30" data-testid="profile-palette-block">
                        <div className="flex items-center gap-2 mb-2 text-xs font-medium">
                          <Sparkles className="size-3.5 text-primary" />
                          Palette suggerita dal tuo logo
                          {extractingPalette && <span className="text-muted-foreground">· estrazione in corso...</span>}
                        </div>
                        {extractedPalette.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex flex-wrap gap-2">
                              {extractedPalette.map((c, i) => (
                                <div key={i} className="flex items-center gap-1.5 rounded-lg border bg-background p-1 pr-2">
                                  <div className="size-6 rounded" style={{ background: c.hex }} title={c.hex} />
                                  <div className="flex flex-col text-[10px] leading-tight">
                                    <span className="font-mono">{c.hex}</span>
                                    <div className="flex gap-1">
                                      <button type="button" className="text-primary hover:underline" onClick={() => applyPaletteColor(c.hex, 'primary_color')} data-testid={`palette-apply-primary-${i}`}>primary</button>
                                      <button type="button" className="text-accent hover:underline" onClick={() => applyPaletteColor(c.hex, 'accent_color')} data-testid={`palette-apply-accent-${i}`}>accent</button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <p className="text-[11px] text-muted-foreground">Click su <strong>primary</strong>/<strong>accent</strong> per applicare il colore ai campi qui sotto. Oppure usa manualmente il picker &darr;</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <Separator />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Colore primario</Label>
                      <ColorPicker value={companyForm.primary_color} onChange={(c) => setCompanyForm({ ...companyForm, primary_color: c })} testid="profile-primary-color" />
                      <p className="text-xs text-muted-foreground">Usato per titoli sezione, header e copertina del PDF.</p>
                    </div>
                    <div className="space-y-2"><Label>Colore accent</Label>
                      <ColorPicker value={companyForm.accent_color} onChange={(c) => setCompanyForm({ ...companyForm, accent_color: c })} testid="profile-accent-color" />
                      <p className="text-xs text-muted-foreground">Usato per dettagli grafici, gradienti copertina e highlight.</p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setCompanyForm(company)}>Annulla</Button>
                    <Button onClick={handleSavebranding} data-testid="profile-save-branding"><Save className="size-4 mr-2" />Salva personalizzazione</Button>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="stamp-signature-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><FileSignature className="size-4 text-primary" />Timbro &amp; Firma del Fornitore</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <ShieldCheck className="size-4" />
                    <AlertTitle>Firma sempre presente sui contratti</AlertTitle>
                    <AlertDescription>
                      Carica qui la scansione del <strong>timbro + firma</strong> del legale rappresentante. Verrà stampata automaticamente nell’area firma del Fornitore su ogni contratto PDF, così non dovrai firmare manualmente ogni volta.
                    </AlertDescription>
                  </Alert>
                  <div className="flex items-center gap-4">
                    <div className="w-48 h-28 rounded-lg border bg-white grid place-items-center overflow-hidden p-2">
                      {companyForm.stamp_signature_data_url ? (
                        <img src={companyForm.stamp_signature_data_url} alt="timbro e firma" className="max-h-full max-w-full object-contain" />
                      ) : (
                        <div className="text-xs text-muted-foreground text-center">
                          <FileSignature className="size-6 mx-auto mb-1" />
                          Nessun timbro caricato
                        </div>
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="text-xs"
                        data-testid="profile-stamp-upload"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 1.5 * 1024 * 1024) { toast.error('Immagine troppo grande (max 1.5MB)'); return; }
                          try {
                            const dataUrl = await readFileAsDataUrl(file);
                            setCompanyForm(f => ({ ...f, stamp_signature_data_url: dataUrl }));
                            toast.success('Timbro & firma caricato');
                          } catch { toast.error('Impossibile leggere il file'); }
                          e.target.value = '';
                        }}
                      />
                      <p className="text-xs text-muted-foreground">Formato consigliato: PNG con sfondo trasparente, 600×280 px circa. Max 1.5MB.</p>
                      {companyForm.stamp_signature_data_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setCompanyForm(f => ({ ...f, stamp_signature_data_url: '' }))}
                          data-testid="profile-stamp-remove"
                        >
                          <X className="size-3.5 mr-1" /> Rimuovi
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleSavebranding} data-testid="profile-save-stamp">
                      <Save className="size-4 mr-2" />Salva timbro &amp; firma
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="size-4" />Layout PDF personalizzato</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <Sparkles className="size-4" />
                    <AlertTitle>Layout su misura per questa azienda</AlertTitle>
                    <AlertDescription>
                      Quando ricevi dall’azienda un layout grafico specifico per il PDF, caricalo qui sotto.
                      Lo staff Scintilla Hub lo trasformerà in un template attivo per <strong>{company.name}</strong>.
                      Tutti i contratti generati useranno automaticamente quel layout (con dati cliente, prodotti e firme dinamici).
                    </AlertDescription>
                  </Alert>
                  <div className="border-2 border-dashed rounded-xl p-6 text-center bg-secondary/30">
                    <Upload className="size-7 mx-auto text-muted-foreground mb-2" />
                    <div className="text-sm font-medium">Trascina qui il layout PDF / PPTX / PSD</div>
                    <div className="text-xs text-muted-foreground mt-1">oppure</div>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => toast.info('Funzione di upload layout in arrivo. Per ora puoi inviarci il file via email e lo integriamo.')} data-testid="profile-layout-upload">
                      <Upload className="size-4 mr-2" />Carica layout personalizzato
                    </Button>
                    <div className="text-[11px] text-muted-foreground mt-3">Formati supportati: PDF, PPTX, PSD, AI · Max 20MB</div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <Card className="sticky top-20">
                <CardHeader className="pb-2"><CardTitle className="text-base">Anteprima brand</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-xl overflow-hidden border" style={{ background: companyForm.primary_color || '#0F766E' }}>
                    <div className="aspect-[1/1.41] p-4 flex flex-col justify-between text-white relative" style={{ backgroundImage: `linear-gradient(135deg, ${companyForm.primary_color || '#0F766E'} 0%, ${companyForm.accent_color || '#14B8A6'} 100%)` }}>
                      <div className="bg-white rounded-md p-2 inline-flex max-w-[140px]">
                        {companyForm.logo_data_url ? <img src={companyForm.logo_data_url} alt="logo" className="max-h-8 object-contain" /> : <div className="font-heading font-semibold text-sm" style={{ color: companyForm.primary_color }}>{companyForm.name}</div>}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Contratto</div>
                        <div className="font-heading text-lg leading-tight mt-1">Soluzione di efficientamento energetico</div>
                        <div className="text-[10px] opacity-80 mt-2">Contratto white-label by {company.name}</div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between"><span className="text-muted-foreground">Primario</span><span className="font-mono">{companyForm.primary_color}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Accent</span><span className="font-mono">{companyForm.accent_color}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Logo</span><span>{companyForm.logo_data_url ? 'Caricato' : 'Non impostato'}</span></div>
                  </div>
                  <Button className="w-full" variant="outline" size="sm" onClick={generateBlank} disabled={generatingBlank}>
                    {generatingBlank ? <Loader2 className="size-4 mr-2 animate-spin" /> : <FileDown className="size-4 mr-2" />}
                    {generatingBlank ? 'Generazione in corso…' : 'Genera PDF di prova'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        )}

        {/* ===== TAB PREVENTIVO (solo Admin Scintilla) ===== */}
        {admin && (
        <TabsContent value="preventivo">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><Sparkles className="size-4" />Testo di copertina e referente commerciale</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FieldI label="Testo introduttivo del preventivo" full>
                    <Textarea
                      rows={4}
                      value={companyForm.intro_text || ''}
                      onChange={(e) => setCompanyForm({ ...companyForm, intro_text: e.target.value })}
                      placeholder="Es. Grazie per la vostra richiesta. Siamo lieti di presentarvi la nostra offerta personalizzata..."
                      data-testid="profile-intro-text"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Compare in copertina, sotto il saluto al cliente. Se vuoto viene usato un testo generico.</p>
                  </FieldI>
                  <Separator />
                  <div className="text-sm font-medium">Referente commerciale (mostrato in copertina)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldI label="Nome e cognome">
                      <Input value={companyForm.contact_name || ''} onChange={(e) => setCompanyForm({ ...companyForm, contact_name: e.target.value })} data-testid="profile-contact-name" placeholder="Marco Bianchi" />
                    </FieldI>
                    <FieldI label="Ruolo / qualifica">
                      <Input value={companyForm.contact_role || ''} onChange={(e) => setCompanyForm({ ...companyForm, contact_role: e.target.value })} placeholder="Consulente Energia" />
                    </FieldI>
                    <FieldI label="Telefono">
                      <Input value={companyForm.contact_phone || ''} onChange={(e) => setCompanyForm({ ...companyForm, contact_phone: e.target.value })} placeholder="347 1234567" />
                    </FieldI>
                    <FieldI label="Email">
                      <Input type="email" value={companyForm.contact_email || ''} onChange={(e) => setCompanyForm({ ...companyForm, contact_email: e.target.value })} placeholder="marco.bianchi@azienda.it" />
                    </FieldI>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><ImageIcon className="size-4" />Immagine di copertina del preventivo</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="rounded-lg overflow-hidden border relative" style={{ aspectRatio: '21/9' }}>
                    {companyForm.quote_cover_data_url ? (
                      <img src={companyForm.quote_cover_data_url} alt="Copertina preventivo" className="w-full h-full object-cover" data-testid="quote-cover-preview" />
                    ) : (
                      <div className="w-full h-full grid place-items-center bg-secondary/40 text-center p-4">
                        <div>
                          <ImageIcon className="size-6 mx-auto text-muted-foreground mb-1" />
                          <div className="text-xs text-muted-foreground">Immagine default: campo fotovoltaico</div>
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-br from-black/60 to-black/20 flex items-end p-3 pointer-events-none">
                      <span className="text-white text-sm font-medium drop-shadow">Soluzione di Efficientamento Energetico</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => quoteCoverRef.current?.click()} data-testid="quote-cover-upload-button">
                      <Upload className="size-4 mr-2" />Carica immagine
                    </Button>
                    <input
                      ref={quoteCoverRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      data-testid="quote-cover-file-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (!file) return;
                        if (file.size > 3 * 1024 * 1024) { toast.error('Immagine troppo grande (max 3MB)'); return; }
                        try {
                          const dataUrl = await readFileAsDataUrl(file);
                          setCompanyForm(f => ({ ...f, quote_cover_data_url: dataUrl }));
                          toast.info('Immagine caricata: ricorda di salvare le impostazioni preventivo');
                        } catch { toast.error('Impossibile leggere il file'); }
                      }}
                    />
                    <span className="text-xs text-muted-foreground">Dimensioni consigliate: <strong>1680×720 px</strong> (proporzioni 21:9) · PNG/JPG · max 3MB</span>
                    {companyForm.quote_cover_data_url && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setCompanyForm(f => ({ ...f, quote_cover_data_url: '' }))} data-testid="quote-cover-reset-button">
                        <X className="size-4 mr-1" />Ripristina default
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Sostituisce l’immagine del campo fotovoltaico nella prima pagina del preventivo. Sull’immagine viene applicata automaticamente una velatura scura per garantire la leggibilità dei testi bianchi.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><Leaf className="size-4" />Coefficienti Business Plan &amp; Impatto Ambientale</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <BadgeInfo className="size-4" />
                    <AlertTitle>Valori usati per il calcolo automatico</AlertTitle>
                    <AlertDescription>
                      Questi parametri vengono usati per pre-compilare il business plan (risparmio annuo, payback, CO<sub>2</sub>, alberi equivalenti) del preventivo.
                      L’operatore potrà comunque modificarli manualmente al momento della generazione.
                    </AlertDescription>
                  </Alert>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldI label="Produzione FV media (h/anno equivalenti)">
                      <Input type="number" step="1" value={companyForm.bp_pv_hours_year ?? 1200}
                        onChange={(e) => setCompanyForm({ ...companyForm, bp_pv_hours_year: Number(e.target.value) })}
                        className="tabular-nums" data-testid="profile-coeff-pv-hours" />
                      <p className="text-xs text-muted-foreground mt-1">Default 1200 h/anno (media italiana centro-sud).</p>
                    </FieldI>
                    <FieldI label="Costo energia elettrica (€/kWh)">
                      <Input type="number" step="0.01" value={companyForm.bp_energy_eur_per_kwh ?? 0.25}
                        onChange={(e) => setCompanyForm({ ...companyForm, bp_energy_eur_per_kwh: Number(e.target.value) })}
                        className="tabular-nums" />
                      <p className="text-xs text-muted-foreground mt-1">Usato per stimare il risparmio annuo.</p>
                    </FieldI>
                    <FieldI label="Tariffa immissione in rete (€/kWh)">
                      <Input type="number" step="0.01" min="0" value={companyForm.bp_feed_in_eur_kwh ?? 0.10}
                        onChange={(e) => setCompanyForm({ ...companyForm, bp_feed_in_eur_kwh: Number(e.target.value) })}
                        className="tabular-nums" data-testid="profile-coeff-feed-in" />
                      <p className="text-xs text-muted-foreground mt-1">Valorizzazione dell’energia immessa in rete (Ritiro Dedicato). Default 0,10 €/kWh.</p>
                    </FieldI>
                    <FieldI label="Fattore emissioni CO₂ (kg/kWh)">
                      <Input type="number" step="0.01" value={companyForm.bp_co2_kg_per_kwh ?? 0.4}
                        onChange={(e) => setCompanyForm({ ...companyForm, bp_co2_kg_per_kwh: Number(e.target.value) })}
                        className="tabular-nums" />
                      <p className="text-xs text-muted-foreground mt-1">Default 0,4 kg CO₂ per kWh evitato.</p>
                    </FieldI>
                    <FieldI label="Assorbimento CO₂ per albero (kg/anno)">
                      <Input type="number" step="1" value={companyForm.bp_co2_kg_per_tree ?? 20}
                        onChange={(e) => setCompanyForm({ ...companyForm, bp_co2_kg_per_tree: Number(e.target.value) })}
                        className="tabular-nums" />
                      <p className="text-xs text-muted-foreground mt-1">Default 20 kg/anno per albero maturo.</p>
                    </FieldI>
                    <FieldI label="Km auto equivalenti per kg CO₂">
                      <Input type="number" step="0.1" value={companyForm.bp_km_per_kg_co2 ?? 5}
                        onChange={(e) => setCompanyForm({ ...companyForm, bp_km_per_kg_co2: Number(e.target.value) })}
                        className="tabular-nums" />
                      <p className="text-xs text-muted-foreground mt-1">Default 5 km per kg CO₂ (auto benzina media).</p>
                    </FieldI>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="pdf-defaults-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><FileText className="size-4" />Impostazioni PDF predefinite</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Alert>
                    <BadgeInfo className="size-4" />
                    <AlertTitle>Preselezione toggle nel dialog di generazione preventivo</AlertTitle>
                    <AlertDescription>
                      Questi valori vengono pre-selezionati ogni volta che generi un nuovo preventivo, evitandoti di modificarli manualmente.
                      Puoi comunque cambiarli caso per caso al momento della generazione.
                    </AlertDescription>
                  </Alert>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <div className="text-sm font-medium">Includi Business Plan</div>
                      <div className="text-xs text-muted-foreground">Sezione con proiezioni economiche (risparmio, payback, KPI).</div>
                    </div>
                    <Switch
                      checked={companyForm.pdf_default_include_bp !== false}
                      onCheckedChange={(v) => setCompanyForm({ ...companyForm, pdf_default_include_bp: !!v })}
                      data-testid="pdf-default-bp"
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <div className="text-sm font-medium">Includi grafico Payback</div>
                      <div className="text-xs text-muted-foreground">Grafico a barre del cash-flow cumulato sui 25 anni (visibile solo se il BP è incluso).</div>
                    </div>
                    <Switch
                      checked={companyForm.pdf_default_include_payback !== false}
                      onCheckedChange={(v) => setCompanyForm({ ...companyForm, pdf_default_include_payback: !!v })}
                      data-testid="pdf-default-payback"
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <div className="text-sm font-medium">Includi Schede Tecniche</div>
                      <div className="text-xs text-muted-foreground">Allega le schede tecniche dei prodotti al PDF preventivo.</div>
                    </div>
                    <Switch
                      checked={companyForm.pdf_default_include_datasheets !== false}
                      onCheckedChange={(v) => setCompanyForm({ ...companyForm, pdf_default_include_datasheets: !!v })}
                      data-testid="pdf-default-datasheets"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><ListChecks className="size-4" />Servizi inclusi nei preventivi</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Textarea
                    rows={8}
                    value={(companyForm.included_services && companyForm.included_services.length > 0)
                      ? companyForm.included_services.join('\n')
                      : ''}
                    onChange={(e) => {
                      const lines = e.target.value.split('\n');
                      // Array vuoto = ripristina la lista standard (null verrebbe ignorato dal backend)
                      setCompanyForm({ ...companyForm, included_services: e.target.value.trim() === '' ? [] : lines });
                    }}
                    placeholder={'Sopralluogo tecnico preliminare\nProgettazione e disegni tecnici\nPratiche burocratiche e autorizzative\nTrasporto e logistica dei materiali\nInstallazione e posa in opera a regola d\'arte\nCollaudo funzionale dell\'impianto\nRilascio Dichiarazione di Conformità (D.M. 37/2008)\nAssistenza pratiche GSE e connessione alla rete'}
                    data-testid="profile-included-services"
                  />
                  <p className="text-xs text-muted-foreground">
                    Un servizio per riga. Questa lista compare nel PDF sotto le “Voci di costo” come “Servizi inclusi nel prezzo”.
                    Se lasci il campo vuoto viene usata la lista standard (mostrata come suggerimento qui sopra).
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2"><Mail className="size-4" />Email preventivi e contratti</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <BadgeInfo className="size-4" />
                    <AlertTitle>Invio documenti via email</AlertTitle>
                    <AlertDescription>
                      Se il tuo indirizzo mittente usa un dominio non verificato sul servizio email, il documento verrà inviato dal mittente di sistema
                      con il nome della tua azienda e le risposte del cliente arriveranno comunque alla tua email.
                      Placeholder disponibili nei testi: <code className="text-xs">{'{cliente} {azienda} {numero} {totale} {referente}'}</code>
                    </AlertDescription>
                  </Alert>
                  <FieldI label="Email mittente">
                    <Input type="email" value={companyForm.sender_email || ''}
                      onChange={(e) => setCompanyForm({ ...companyForm, sender_email: e.target.value })}
                      placeholder="preventivi@tuaazienda.it" data-testid="profile-sender-email" />
                  </FieldI>
                  <Separator />
                  <div className="text-sm font-medium">Email preventivi</div>
                  <FieldI label="Oggetto (opzionale)">
                    <Input value={companyForm.email_subject_template || ''}
                      onChange={(e) => setCompanyForm({ ...companyForm, email_subject_template: e.target.value })}
                      placeholder="Il tuo preventivo - {azienda}" data-testid="profile-email-subject" />
                  </FieldI>
                  <FieldI label="Testo (opzionale)">
                    <Textarea rows={5} value={companyForm.email_body_template || ''}
                      onChange={(e) => setCompanyForm({ ...companyForm, email_body_template: e.target.value })}
                      placeholder={'Gentile {cliente},\n\nin allegato trovi il preventivo richiesto per la tua soluzione di efficientamento energetico.\n\nRestiamo a disposizione per qualsiasi chiarimento.\n\nCordiali saluti,\n{referente}\n{azienda}'}
                      data-testid="profile-email-body" />
                  </FieldI>
                  <Separator />
                  <div className="text-sm font-medium">Email contratti</div>
                  <FieldI label="Oggetto (opzionale)">
                    <Input value={companyForm.email_subject_contract_template || ''}
                      onChange={(e) => setCompanyForm({ ...companyForm, email_subject_contract_template: e.target.value })}
                      placeholder="Il tuo contratto - {azienda}" data-testid="profile-email-subject-contract" />
                  </FieldI>
                  <FieldI label="Testo (opzionale)">
                    <Textarea rows={5} value={companyForm.email_body_contract_template || ''}
                      onChange={(e) => setCompanyForm({ ...companyForm, email_body_contract_template: e.target.value })}
                      placeholder={'Gentile {cliente},\n\nin allegato trovi il contratto {numero} relativo alla tua soluzione di efficientamento energetico.\n\nTi invitiamo a verificarne il contenuto e a conservarne una copia.\n\nCordiali saluti,\n{referente}\n{azienda}'}
                      data-testid="profile-email-body-contract" />
                    <p className="text-xs text-muted-foreground mt-1">Se lasciati vuoti vengono usati testi standard con il branding aziendale.</p>
                  </FieldI>
                </CardContent>
              </Card>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCompanyForm(company)}>Annulla</Button>
                <Button onClick={handleSaveCompany} disabled={savingCompany} data-testid="profile-save-preventivo"><Save className="size-4 mr-2" />Salva impostazioni preventivo</Button>
              </div>
            </div>

            <div>
              <Card className="lg:sticky lg:top-20">
                <CardHeader className="pb-2"><CardTitle className="text-base">Come apparirà nel PDF</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Copertina</div>
                    <div className="rounded-lg border p-3 bg-secondary/40">
                      <div className="text-xs font-medium mb-1">Gentile [Nome Cliente],</div>
                      <div className="text-xs text-muted-foreground italic">
                        {companyForm.intro_text || 'Grazie per la vostra richiesta. Siamo lieti di presentarvi la nostra offerta personalizzata...'}
                      </div>
                    </div>
                  </div>
                  {(companyForm.contact_name || companyForm.contact_email) && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Il tuo referente</div>
                      <div className="rounded-lg border p-3 bg-secondary/40 space-y-0.5">
                        {companyForm.contact_name && <div className="font-medium text-xs">{companyForm.contact_name}</div>}
                        {companyForm.contact_role && <div className="text-xs text-muted-foreground">{companyForm.contact_role}</div>}
                        {companyForm.contact_phone && <div className="text-xs">Tel. {companyForm.contact_phone}</div>}
                        {companyForm.contact_email && <div className="text-xs">{companyForm.contact_email}</div>}
                      </div>
                    </div>
                  )}
                  <Button className="w-full" variant="outline" size="sm" onClick={handleSaveCompany}>
                    <Save className="size-4 mr-2" />Salva e prova
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        )}
      </Tabs>

      {/* Product dialog */}
      <Dialog open={productOpen} onOpenChange={setProductOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editProductId ? 'Modifica prodotto' : 'Nuovo prodotto'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Categoria *</Label>
              <Select value={productForm.category} onValueChange={(v) => setProductForm({ ...productForm, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(options.product_categories || []).map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Marca</Label><Input value={productForm.brand} onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })} /></div>
            <div className="space-y-2"><Label>Modello</Label><Input value={productForm.model} onChange={(e) => setProductForm({ ...productForm, model: e.target.value })} /></div>
            <div className="space-y-2"><Label>Potenza / Capacità</Label><Input value={productForm.power} onChange={(e) => setProductForm({ ...productForm, power: e.target.value })} placeholder="Es. 6 kW · 465 Wp" /></div>
            <div className="space-y-2 md:col-span-2"><Label>Descrizione</Label><Textarea rows={2} value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} /></div>
            <div className="space-y-2"><Label>Prezzo unitario (€)</Label><Input type="number" step="0.01" min="0" value={productForm.unit_price} onChange={(e) => setProductForm({ ...productForm, unit_price: e.target.value })} className="tabular-nums" /></div>
            <div className="flex flex-col justify-center gap-3 mt-4 md:mt-0">
              <div className="flex items-center gap-3"><Switch checked={productForm.active} onCheckedChange={(v) => setProductForm({ ...productForm, active: v })} data-testid="product-active-switch" /><Label>Attivo (visibile nel catalogo)</Label></div>
              <div className="flex items-start gap-3">
                <Switch checked={!!productForm.is_custom} onCheckedChange={(v) => setProductForm({ ...productForm, is_custom: v })} data-testid="product-iscustom-switch" className="mt-1" />
                <div>
                  <Label className="cursor-pointer" onClick={() => setProductForm({ ...productForm, is_custom: !productForm.is_custom })}>Voce personalizzabile</Label>
                  <p className="text-xs text-muted-foreground">L&apos;operatore potrà modificare descrizione, marca, modello e prezzo quando inserisce questa voce in un contratto o preventivo.</p>
                </div>
              </div>
            </div>

            <div className="space-y-2 md:col-span-2"><Separator /></div>
            <div className="md:col-span-2 text-xs uppercase tracking-wider text-muted-foreground">Dettagli tecnici per il preventivo</div>
            <div className="space-y-2"><Label>Garanzia</Label><Input value={productForm.warranty_years || ''} onChange={(e) => setProductForm({ ...productForm, warranty_years: e.target.value })} placeholder="Es. 25 anni" /></div>
            <div className="space-y-2"><Label>Efficienza</Label><Input value={productForm.efficiency || ''} onChange={(e) => setProductForm({ ...productForm, efficiency: e.target.value })} placeholder="Es. 21.5%" /></div>
            <div className="space-y-2"><Label>Produzione annua stimata (kWh)</Label>
              <Input type="number" step="1" min="0" value={productForm.annual_production_kwh || 0} onChange={(e) => setProductForm({ ...productForm, annual_production_kwh: Number(e.target.value) })} className="tabular-nums" placeholder="Es. 7200 (per FV)" />
              <p className="text-xs text-muted-foreground">Se lasciato vuoto, viene stimato dai coefficienti azienda.</p>
            </div>
            <div className="space-y-2"><Label>Note interne</Label><Textarea rows={2} value={productForm.notes} onChange={(e) => setProductForm({ ...productForm, notes: e.target.value })} /></div>

            <div className="space-y-2 md:col-span-2"><Separator /></div>
            <div className="md:col-span-2 text-xs uppercase tracking-wider text-muted-foreground">Immagine di copertina del prodotto</div>
            <div className="space-y-2 md:col-span-2">
              <div className="flex flex-col sm:flex-row gap-3 items-start">
                {productForm.thumbnail_data_url ? (
                  <div className="flex-1 rounded-lg border p-3 bg-secondary/30 flex items-center gap-3 min-w-0">
                    <img src={productForm.thumbnail_data_url} alt="" className="size-16 rounded-md border bg-white object-contain p-1 shrink-0" data-testid="product-thumbnail-preview" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm">Immagine caricata</div>
                      <div className="text-xs text-muted-foreground">Mostrata nella scheda componente del preventivo</div>
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => setProductForm({ ...productForm, thumbnail_data_url: '' })} data-testid="product-remove-thumbnail">
                      <X className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex-1 rounded-lg border-2 border-dashed p-4 bg-secondary/20">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="text-xs"
                      data-testid="product-thumbnail-upload"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) { toast.error('Immagine troppo grande (max 2MB)'); return; }
                        try {
                          const dataUrl = await readFileAsDataUrl(file);
                          setProductForm(f => ({ ...f, thumbnail_data_url: dataUrl }));
                        } catch { toast.error('Impossibile leggere il file'); }
                      }}
                    />
                    <p className="text-xs text-muted-foreground mt-2">PNG/JPG/WebP · Consigliato: quadrata ~600×600 px · Max 2MB. Compare accanto al prodotto nel preventivo.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 md:col-span-2"><Separator /></div>
            <div className="md:col-span-2 text-xs uppercase tracking-wider text-muted-foreground">Schede tecniche (PDF)</div>
            <div className="space-y-3 md:col-span-2">
              <Label className="flex items-center gap-2"><Paperclip className="size-4" />Allega una o più schede tecniche del produttore</Label>

              {/* Elenco schede già caricate */}
              {(productForm.datasheets && productForm.datasheets.length > 0) && (
                <ul className="space-y-2" data-testid="product-datasheets-list">
                  {productForm.datasheets.map((ds, idx) => (
                    <li key={idx} className="rounded-lg border p-3 bg-secondary/30 flex items-center gap-2 min-w-0" data-testid={`product-datasheet-item-${idx}`}>
                      <FileText className="size-5 text-primary shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm truncate">{ds.filename || `scheda_${idx + 1}.pdf`}</div>
                        <div className="text-xs text-muted-foreground">
                          {Math.round(((ds.data_url || '').length * 0.75) / 1024)} KB · Posizione {idx + 1} di {productForm.datasheets.length}
                        </div>
                      </div>
                      {/* Riordina */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => moveDatasheet(idx, -1)}
                        disabled={idx === 0}
                        title="Sposta su"
                        aria-label="Sposta la scheda in alto"
                        data-testid={`product-datasheet-up-${idx}`}
                      >
                        <ArrowUp className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => moveDatasheet(idx, +1)}
                        disabled={idx === productForm.datasheets.length - 1}
                        title="Sposta giù"
                        aria-label="Sposta la scheda in basso"
                        data-testid={`product-datasheet-down-${idx}`}
                      >
                        <ArrowDown className="size-4" />
                      </Button>
                      {/* Anteprima */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => previewDatasheet(ds)}
                        title="Anteprima PDF"
                        aria-label="Apri anteprima scheda tecnica"
                        data-testid={`product-datasheet-preview-${idx}`}
                      >
                        <Eye className="size-4" />
                      </Button>
                      {/* Sostituisci (input file nascosto) */}
                      <label className="inline-flex" title="Sostituisci scheda">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          asChild
                          data-testid={`product-datasheet-replace-${idx}`}
                        >
                          <span aria-label="Sostituisci scheda tecnica"><RefreshCw className="size-4" /></span>
                        </Button>
                        <input
                          type="file"
                          accept="application/pdf"
                          className="sr-only"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            await replaceDatasheet(idx, f);
                            e.target.value = '';
                          }}
                        />
                      </label>
                      {/* Rimuovi */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setProductForm(f => ({ ...f, datasheets: f.datasheets.filter((_, i) => i !== idx) }))}
                        data-testid={`product-remove-datasheet-${idx}`}
                        aria-label="Rimuovi scheda tecnica"
                        title="Rimuovi"
                      >
                        <X className="size-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Area drop + upload */}
              <div
                className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${datasheetsDrag ? 'border-primary bg-primary/5' : 'bg-secondary/20 hover:bg-secondary/30'}`}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDatasheetsDrag(true); }}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDatasheetsDrag(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDatasheetsDrag(false); }}
                onDrop={async (e) => {
                  e.preventDefault(); e.stopPropagation(); setDatasheetsDrag(false);
                  const files = Array.from(e.dataTransfer?.files || []);
                  await addDatasheetFiles(files);
                }}
                data-testid="product-datasheet-dropzone"
              >
                <Upload className="size-6 mx-auto text-muted-foreground mb-2" />
                <div className="text-sm">
                  <strong>Trascina qui i PDF</strong> oppure
                  <label className="inline-flex items-center ml-2 underline text-primary cursor-pointer">
                    sfoglia dal computer
                    <input
                      type="file"
                      accept="application/pdf"
                      multiple
                      className="sr-only"
                      data-testid="product-datasheet-upload"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        await addDatasheetFiles(files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Solo PDF · Max 5MB per file · Puoi caricare più schede: verranno tutte concatenate in coda al preventivo.</p>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setProductOpen(false)}>Annulla</Button><Button onClick={handleSaveProduct} data-testid="product-save-button">{editProductId ? 'Salva' : 'Crea'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User dialog */}
      <Dialog open={userOpen} onOpenChange={setUserOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editUserId ? 'Modifica utente' : 'Nuovo utente'} - {company.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldI label="Nome e Cognome *"><Input value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} /></FieldI>
            <FieldI label="Email *"><Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} disabled={!!editUserId} /></FieldI>
            <FieldI label="Telefono"><Input value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} /></FieldI>
            <FieldI label={`Password ${editUserId ? '(lascia vuoto per non cambiare)' : '*'}`}><Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} /></FieldI>
            <FieldI label="Ruolo">
              <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="partner">B.O. (Back Office)</SelectItem>
                  <SelectItem value="area_manager">Area Manager</SelectItem>
                  <SelectItem value="agent">Agente</SelectItem>
                </SelectContent>
              </Select>
            </FieldI>
            <FieldI label="Stato">
              <Select value={userForm.status} onValueChange={(v) => setUserForm({ ...userForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pending">In attesa</SelectItem><SelectItem value="active">Attivo</SelectItem><SelectItem value="disabled">Disabilitato</SelectItem></SelectContent>
              </Select>
            </FieldI>
            {userForm.role === 'agent' && (
              <div className="md:col-span-2 rounded-lg border p-3 bg-secondary/30 space-y-3" data-testid="agent-permissions-block">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Permessi agente</div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-sm">Può inviare email a clienti</Label>
                    <p className="text-xs text-muted-foreground">Invio di preventivi e contratti via email dal gestionale.</p>
                  </div>
                  <Switch
                    checked={(userForm.agent_permissions?.can_send_email) !== false}
                    onCheckedChange={(v) => setUserForm({ ...userForm, agent_permissions: { ...(userForm.agent_permissions || {}), can_send_email: v } })}
                    data-testid="agent-perm-send-email"
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-sm">Può vedere i prezzi di listino</Label>
                    <p className="text-xs text-muted-foreground">Se disattivato, i prezzi sono nascosti nel catalogo; i PDF vengono comunque calcolati con i prezzi corretti.</p>
                  </div>
                  <Switch
                    checked={(userForm.agent_permissions?.can_view_prices) !== false}
                    onCheckedChange={(v) => setUserForm({ ...userForm, agent_permissions: { ...(userForm.agent_permissions || {}), can_view_prices: v } })}
                    data-testid="agent-perm-view-prices"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setUserOpen(false)}>Annulla</Button><Button onClick={handleSaveUser}>{editUserId ? 'Salva' : 'Crea'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Risultato import Excel */}
      <Dialog open={!!importResult} onOpenChange={(v) => { if (!v) setImportResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="import-result-dialog">
          <DialogHeader><DialogTitle>Risultato import listino</DialogTitle></DialogHeader>
          {importResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg border p-3">
                  <div className="text-2xl font-semibold tabular-nums text-primary">{importResult.created}</div>
                  <div className="text-xs text-muted-foreground">Creati</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-2xl font-semibold tabular-nums">{importResult.updated}</div>
                  <div className="text-xs text-muted-foreground">Aggiornati</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className={`text-2xl font-semibold tabular-nums ${importResult.error_count ? 'text-destructive' : ''}`}>{importResult.error_count}</div>
                  <div className="text-xs text-muted-foreground">Errori</div>
                </div>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="max-h-52 overflow-y-auto rounded-lg border divide-y text-sm">
                  {importResult.errors.map((er, i) => (
                    <div key={i} className="px-3 py-1.5 flex gap-2">
                      <Badge variant="outline" className="shrink-0 text-[10px]">Riga {er.row}</Badge>
                      <span className="text-xs text-destructive">{er.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={() => setImportResult(null)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const FieldI = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? 'md:col-span-full' : ''}`}>
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);
